import React, { useState, useRef, useEffect } from 'react';
import Style from './GraphSearch.module.css';

const GraphSearch = ({ nodes, edges, onNodeHighlight, onExpandToNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  
  // Animation timing (in ms)
  const ANIMATION_DURATION = 300;
  
  // Load recent searches from localStorage if available
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const saved = window.localStorage.getItem('graphSearchHistory');
        if (saved) {
          setRecentSearches(JSON.parse(saved).slice(0, 5)); // Keep only 5 most recent
        }
      } catch (e) {
        console.error('Error loading search history:', e);
      }
    }
  }, []);
  
  // Toggle search box visibility with animation
  const toggleSearch = () => {
    if (!isOpen) {
      setIsOpen(true);
      // Focus the input after animation completes
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, ANIMATION_DURATION / 2);
    } else {
      setIsOpen(false);
      setSearchTerm('');
      setSearchResults([]);
    }
  };

  // Handle input changes
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsSearching(true);
    
    if (value.trim() === '') {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    
    // Debounce search for better performance
    const timer = setTimeout(() => {
      // Find matching nodes (only leaf nodes)
      const results = nodes.filter(node => {
        // Only search in non-root, non-parent nodes (leaf nodes)
        if (node.data.isRootNode || node.data.hasChildren) {
          return false;
        }
        
        // Search in label (case insensitive)
        return node.data.label.toLowerCase().includes(value.toLowerCase());
      });
      
      setSearchResults(results);
      setIsSearching(false);
    }, 150);
    
    return () => clearTimeout(timer);
  };
  
  // Add a search term to recent searches
  const addToRecentSearches = (nodeId, label) => {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        // Create new recent search entry
        const newSearch = { 
          id: nodeId, 
          label, 
          timestamp: Date.now() 
        };
        
        // Update state with the new search
        setRecentSearches(prev => {
          // Remove any existing entry with the same ID
          const filtered = prev.filter(item => item.id !== nodeId);
          // Add new entry at the beginning
          const updated = [newSearch, ...filtered].slice(0, 5);
          
          // Save to localStorage
          window.localStorage.setItem('graphSearchHistory', JSON.stringify(updated));
          
          return updated;
        });
      } catch (e) {
        console.error('Error saving search history:', e);
      }
    }
  };
  
  // Handle result selection
  const handleResultSelect = (node) => {
    // Call the highlight function directly with the node ID
    onNodeHighlight(node.id);
    
    // If the node is hidden (inside a collapsed parent), expand to it
    if (node.hidden || isNodeInCollapsedParent(node.id, nodes)) {
      onExpandToNode(node.id);
    }
    
    // Add to recent searches
    addToRecentSearches(node.id, node.data.label);
    
    // Clear search
    setSearchTerm('');
    setSearchResults([]);
    
    // Close the search
    setIsOpen(false);
  };
  
  // Handle clicking a recent search item
  const handleRecentSearchClick = (searchItem) => {
    // Find the node by id
    const node = nodes.find(n => n.id === searchItem.id);

    if (node) {
      // Call the highlight function directly with the node ID
      onNodeHighlight(node.id);
      
      // If the node is hidden (inside a collapsed parent), expand to it
      if (node.hidden || isNodeInCollapsedParent(node.id, nodes)) {
        onExpandToNode(node.id);
      }
      
      // Close the search
      setIsOpen(false);
      setSearchTerm('');
      setSearchResults([]);
      
      // Bring this item to the top of recent searches
      addToRecentSearches(node.id, node.data.label);
    } else {
      console.error('Node not found for ID:', searchItem.id);
      
      // Node no longer exists, remove from recent searches
      const updatedSearches = recentSearches.filter(item => item.id !== searchItem.id);
      setRecentSearches(updatedSearches);
      
      // Update localStorage
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          window.localStorage.setItem('graphSearchHistory', JSON.stringify(updatedSearches));
        } catch (e) {
          console.error('Error updating search history:', e);
        }
      }
      
      alert(`Cannot find the field "${searchItem.label}" in the current graph.`);
    }
  };
  
  // Check if a node is inside a collapsed parent (recursively)
  const isNodeInCollapsedParent = (nodeId, allNodes) => {
    const node = allNodes.find(n => n.id === nodeId);
    if (!node) return false;
    
    // If node has no parent, it's not in a collapsed parent
    if (!node.data.parentId) return false;
    
    // Find the parent node
    const parentNode = allNodes.find(n => n.id === node.data.parentId);
    if (!parentNode) return false;
    
    // Check if the parent is collapsed or hidden
    if (parentNode.data.isCollapsed || parentNode.hidden) {
      return true;
    }
    
    // Recursively check the parent's parent
    return isNodeInCollapsedParent(parentNode.id, allNodes);
  };
  
  // Get the parent path for display in the search results
  const getParentPath = (nodeId, allNodes) => {
    const paths = [];
    let currentNodeId = nodeId;
    
    // Traverse up the parent chain
    while (true) {
      const lookupId = currentNodeId;
      const node = allNodes.find(n => n.id === lookupId);
      if (!node || !node.data.parentId) break;
      
      const parentNode = allNodes.find(n => n.id === node.data.parentId);
      if (!parentNode) break;
      
      paths.unshift(parentNode.data.label);
      currentNodeId = parentNode.id;
    }
    
    return paths.join(' > ');
  };
  
  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isOpen && 
        !e.target.closest('.search-container') && 
        !e.target.closest('.search-results')
      ) {
        setIsOpen(false);
        setSearchTerm('');
        setSearchResults([]);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);


  return (
    <div className={Style.root}>
      <div className={`search-container ${Style.bar}`}>
        {/* Search Icon Button with Animation */}
        <button
          type="button"
          className={`${Style.toggle} ${isOpen ? Style.toggleOpen : ''}`}
          onClick={toggleSearch}
          aria-label="Search fields"
        >
          <i className={`${isOpen ? 'uil-times' : 'uil-search'} ${Style.toggleIcon}`} aria-hidden="true" />
        </button>
        
        {/* Search Input with Animation */}
        <div className={`${Style.inputWrap} ${isOpen ? Style.inputWrapOpen : ''}`}>
          <div className="position-relative">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search fields..."
              value={searchTerm}
              onChange={handleSearchChange}
              className={`form-control ${Style.input}`}
            />
            
            {/* Loading indicator */}
            {isSearching && (
              <div className={Style.inputSpinner}>
                <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" />
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Search Results with Animation */}
      {isOpen && searchResults.length > 0 && (
        <div 
          ref={resultsRef}
          className={`search-results ${Style.dropdown} ${Style.dropdownScroll}`}
        >
          <div className={Style.dropdownHeader}>
            <p className={Style.dropdownHeaderText}>
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
            </p>
          </div>
          <ul className={Style.list}>
            {searchResults.map(node => (
              <li 
                key={node.id}
                className={`${Style.item} ${Style.itemResult}`}
                onClick={() => handleResultSelect(node)}
              >
                <div className="d-flex align-items-start">
                  <div className={Style.itemText}>
                    <div className={Style.itemLabel}>{node.data.label}</div>
                    <div className={Style.itemPath}>
                      {/* Show the parent hierarchy */}
                      {getParentPath(node.id, nodes)}
                    </div>
                  </div>
                  <div className={Style.itemArrow}>
                    <i className="uil-arrow-right" aria-hidden="true" />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Recent Searches */}
      {isOpen && searchTerm === '' && recentSearches.length > 0 && (
        <div className={Style.dropdown}>
          <div className={Style.dropdownHeader}>
            <p className={Style.dropdownHeaderText}>Recent searches</p>
          </div>
          <ul className={Style.list}>
            {recentSearches.map(item => (
              <li 
                key={item.id}
                className={Style.item}
                onClick={() => handleRecentSearchClick(item)}
              >
                <div className="d-flex align-items-center">
                  <i className={`uil-history ${Style.recentIcon}`} aria-hidden="true" />
                  <span className={Style.recentLabel}>{item.label}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* No Results Message */}
      {isOpen && searchTerm !== '' && searchResults.length === 0 && !isSearching && (
        <div className={`${Style.dropdown} ${Style.empty}`}>
          <div className="text-center">
            <i className={`uil-exclamation-circle ${Style.emptyIcon}`} aria-hidden="true" />
            <p className={Style.emptyTitle}>No results found for "{searchTerm}"</p>
            <p className={Style.emptyHint}>Try different keywords or search for a field name</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GraphSearch;
